import React from 'react';
import SiteLayout from '../components/SiteLayout';
import { BookOpen, FileText, Video, ArrowRight, Layout, Loader2, Clock, Download, AlertCircle, CheckSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { useCMSContent } from '../hooks/useCMSContent';

// Icon mapping for dynamic icon rendering from CMS
const iconMap = {
  BookOpen, FileText, Video, Layout, CheckSquare
};

// Color mapping for resource types
const colorMap = {
  guides: { bg: 'bg-blue-500/10', text: 'text-blue-600' },
  whitepapers: { bg: 'bg-purple-500/10', text: 'text-purple-600' },
  webinars: { bg: 'bg-orange-500/10', text: 'text-orange-600' },
  templates: { bg: 'bg-green-500/10', text: 'text-green-600' },
  casestudies: { bg: 'bg-teal-500/10', text: 'text-teal-600' }
};

const ResourcesPage = () => {
  const { content: cmsContent, loading } = useCMSContent('resources-page');

  if (loading) {
    return (
      <SiteLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </SiteLayout>
    );
  }

  // CMS content - no fallbacks, must be configured in CMS
  const hero = cmsContent?.hero;
  const categories = cmsContent?.categories;
  const newsletter = cmsContent?.newsletter;

  // Show error if CMS content is not configured
  if (!hero || !categories) {
    return (
      <SiteLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center max-w-md">
            <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">CMS Content Required</h2>
            <p className="text-muted-foreground mb-4">
              Resources page content is not configured. Please add content to the CMS document: <code className="bg-muted px-1 rounded">cms_content/resources-page</code>
            </p>
            <p className="text-sm text-muted-foreground">
              Required fields: hero, categories
            </p>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      {hero && (
        <section className="py-20 bg-muted/30">
          <div className="container-wide text-center">
            {hero.title && (
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                {hero.title}
              </h1>
            )}
            {hero.subtitle && (
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                {hero.subtitle}
              </p>
            )}
            {hero.heroImage && (
              <div className="mt-12 relative mx-auto max-w-5xl rounded-xl overflow-hidden shadow-2xl border border-border/50">
                <img
                  src={hero.heroImage}
                  alt={hero.heroImageAlt || ''}
                  className="w-full h-auto object-cover"
                />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Resources by Category */}
      {categories && categories.map((category, catIndex) => {
        const CategoryIcon = iconMap[category.icon] || FileText;
        const colors = colorMap[category.id] || colorMap.guides;

        return (
          <section key={catIndex} className="py-16 border-b border-border last:border-b-0">
            <div className="container-wide">
              <div className="flex items-center gap-3 mb-8">
                <div className={`w-10 h-10 ${colors.bg} rounded-lg flex items-center justify-center`}>
                  <CategoryIcon className={`w-5 h-5 ${colors.text}`} />
                </div>
                <h2 className="text-2xl font-bold">{category.name}</h2>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {(category.resources || []).map((resource, index) => (
                  <div key={index} className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all group">
                    {resource.image ? (
                      <div className="h-40 overflow-hidden relative">
                        <img
                          src={resource.image}
                          alt={resource.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                          <div className={`text-white p-2 rounded-lg ${colors.bg} backdrop-blur-sm`}>
                            <CategoryIcon size={20} className={colors.text} />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className={`h-40 ${colors.bg} flex items-center justify-center`}>
                        <CategoryIcon size={48} className={colors.text} />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`text-xs font-bold ${colors.text} uppercase tracking-wider`}>{resource.type}</div>
                        {resource.featured && (
                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">Featured</span>
                        )}
                      </div>
                      <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors">{resource.title}</h3>
                      <p className="text-muted-foreground mb-4 text-sm">
                        {resource.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {resource.readTime && (
                            <>
                              <Clock size={12} />
                              <span>{resource.readTime} read</span>
                            </>
                          )}
                          {resource.duration && (
                            <>
                              <Clock size={12} />
                              <span>{resource.duration}</span>
                            </>
                          )}
                          {resource.format && (
                            <>
                              <Download size={12} />
                              <span>{resource.format}</span>
                            </>
                          )}
                        </div>
                        <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-hover">
                          {resource.type === 'Webinar' ? 'Watch' : 'Read'} <ArrowRight size={16} className="ml-1" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* Newsletter Section */}
      {newsletter && (
        <section className="py-20 bg-primary text-white">
          <div className="container-wide text-center">
            {newsletter.title && <h2 className="text-3xl font-bold mb-4">{newsletter.title}</h2>}
            {newsletter.description && (
              <p className="text-lg opacity-90 mb-8 max-w-xl mx-auto">
                {newsletter.description}
              </p>
            )}
            {newsletter.placeholder && newsletter.button && (
              <div className="flex gap-4 justify-center max-w-md mx-auto">
                <input
                  type="email"
                  placeholder={newsletter.placeholder}
                  className="flex-1 px-4 py-3 rounded-lg text-foreground bg-white border-0 focus:ring-2 focus:ring-white/50"
                />
                <Button size="lg" className="bg-white text-primary hover:bg-white/90">
                  {newsletter.button}
                </Button>
              </div>
            )}
          </div>
        </section>
      )}
    </SiteLayout>
  );
};

export default ResourcesPage;
